import React, { useEffect, useLayoutEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';

export interface IFormProps extends IBaseElementProps {
	children?: any;
	style?: 'Default' | 'Light';
	alignment?: 'Left' | 'Center';
}

/**
 * Form
 * @block
 * @requiresHooks
 */

const Form: React.FC<IFormProps> = ({
	id,
	className,
	classNames = [],
	children,
	style = 'Default',
	alignment = 'Left',
}) => {
	const ref = useRef<HTMLDivElement>(null);

	// Adds focus classes to fields wrappers
	useEffect(() => {
		let fields;

		const handleFocus = (event: FocusEvent) => {
			const el = event.target as HTMLElement;
			const target =
				el.closest('.gform-grid-col') || el.closest('.gfield');

			target.classList.add('input-focused');
		};

		const handleBlur = (event: FocusEvent) => {
			const el = event.target as HTMLElement;
			const target = el.closest('.gform-grid-col') || el.closest('.gfield');

			target.classList.remove('input-focused');
		};

		const handleChange = (event: any) => {
			const target = event.target as HTMLInputElement;
			const { value } = target;
			const wrapper = target.closest('.gform-grid-col') || target.closest('.gfield');
			if (value) {
				wrapper?.classList.add('input-filled');
			} else {
				wrapper?.classList.remove('input-filled');
			}
		};

		const setListeners = () => {
			fields = ref.current?.querySelectorAll(
				'input, textarea, select',
			) as NodeListOf<HTMLInputElement>;

			fields.forEach((field) => {
				field.addEventListener('focus', handleFocus);
				field.addEventListener('blur', handleBlur);
				field.addEventListener('change', handleChange);

				handleChange({
					target: field,
				});
			});
		};

		const { document } = window;
		// listeners need to be added once children are rendered
		// check if readyState is already set, ContentLoaded already fired. This is for Modals.
		if (document.readyState !== 'loading') {
			setListeners();
		} else {
			document.addEventListener('DOMContentLoaded', setListeners);
		}

		return () => {
			document.removeEventListener('DOMContentLoaded', setListeners);

			fields?.forEach((field) => {
				field.removeEventListener('focus', handleFocus);
				field.removeEventListener('blur', handleBlur);
				field.removeEventListener('change', handleChange);
			});
		};
	}, []);
	return (
		<div
			className={
				className ||
				cn(
					'form-wrapper',
					style == 'Light' && 'form-wrapper--light',
					alignment == 'Center' && 'form-wrapper--centered',
					...classNames,
				)
			}
			ref={ref}
			id={id}
		>
			{children}
		</div>
	);
};

export default Form;
